import { useRef, useCallback, useEffect } from 'react';
import {
    MDXEditor,
    headingsPlugin,
    listsPlugin,
    quotePlugin,
    linkPlugin,
    linkDialogPlugin,
    markdownShortcutPlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    BoldItalicUnderlineToggles,
    StrikeThroughSupSubToggles,
    BlockTypeSelect,
    ListsToggle,
    CreateLink,
    InsertThematicBreak,
    UndoRedo,
    Separator,
    codeBlockPlugin,
    InsertCodeBlock,
    tablePlugin,
    InsertTable
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { GlobalStyles } from '@mui/material';
import { MDXEditorWrapper, MDXViewerWrapper } from './styled';
import { ReactComponent as AttachmentIcon } from '../../assets/icons/attachment2-icon.svg';
import {
    mermaidCodeBlockDescriptor,
    mermaidReadOnlyDescriptor
} from './MermaidBlock';
import {
    defaultCodeBlockDescriptor,
    readOnlyCodeBlockDescriptor
} from './CodeBlock';
import { sanitizeForMdx } from './sanitizeForMdx';

// Global overrides for the Radix Select dropdowns used in MDXEditor's toolbar
// (e.g. BlockTypeSelect). They render in a portal so can't be scoped locally.
const mdxPortalStyles = (
    <GlobalStyles
        styles={{
            '[class*="selectContent"][data-radix-popper-content-wrapper] *, [data-radix-select-viewport]':
                {
                    backgroundColor: '#1E202A !important',
                    color: '#FFFFFF !important'
                },
            '[class*="selectItem"][data-highlighted]': {
                backgroundColor: '#292B36 !important'
            },
            '[class*="selectContent"]': {
                backgroundColor: '#1E202A !important',
                border: '1px solid #26262B !important',
                zIndex: '10000 !important'
            }
        }}
    />
);

const MarkdownEditor = ({
    value,
    onChange,
    disabled,
    readOnly,
    onAttachmentClick,
    height,
    inline
}) => {
    const editorRef = useRef(null);
    const lastExternalValue = useRef(value);
    const wrapperRef = useRef(null);

    // Sync externally-driven value changes into the editor instance.
    // Skipped in readOnly mode since there's no editor ref to update.
    useEffect(() => {
        if (readOnly) return;
        if (editorRef.current && value !== lastExternalValue.current) {
            lastExternalValue.current = value;
            editorRef.current.setMarkdown(sanitizeForMdx(value || ''));
        }
    }, [value, readOnly]);

    const handleChange = useCallback(
        (newMarkdown) => {
            lastExternalValue.current = newMarkdown;
            if (onChange) onChange(newMarkdown);
        },
        [onChange]
    );

    // Intercept paste so plain-text clipboard content is inserted as markdown
    // rather than triggering the browser's default rich-text paste.
    useEffect(() => {
        if (readOnly) return;
        const el = wrapperRef.current;
        if (!el) return;
        const handlePaste = (e) => {
            if (!editorRef.current || disabled) return;
            const text = e.clipboardData?.getData('text/plain');
            if (!text) return;
            e.preventDefault();
            e.stopPropagation();
            editorRef.current.insertMarkdown(text);
        };
        el.addEventListener('paste', handlePaste, true);
        return () => el.removeEventListener('paste', handlePaste, true);
    }, [disabled, readOnly]);

    const toolbarContents = useCallback(
        () => (
            <>
                <UndoRedo />
                <Separator />
                <BlockTypeSelect />
                <Separator />
                <BoldItalicUnderlineToggles />
                <StrikeThroughSupSubToggles options={['strikethrough']} />
                <Separator />
                <ListsToggle />
                <Separator />
                <CreateLink />
                <InsertThematicBreak />
                <InsertCodeBlock />
                <InsertTable />
                {onAttachmentClick && (
                    <>
                        <Separator />
                        <button
                            className="mdx-attachment-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                onAttachmentClick();
                            }}
                            title="Add Attachment"
                            type="button"
                        >
                            <AttachmentIcon width={18} height={18} />
                        </button>
                    </>
                )}
            </>
        ),
        [onAttachmentClick]
    );

    if (readOnly) {
        const plugins = [
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            linkPlugin(),
            thematicBreakPlugin(),
            codeBlockPlugin({
                defaultCodeBlockLanguage: '',
                codeBlockEditorDescriptors: [
                    mermaidReadOnlyDescriptor,
                    readOnlyCodeBlockDescriptor
                ]
            }),
            tablePlugin()
        ];

        return (
            <MDXViewerWrapper
                data-color-mode="dark"
                style={inline ? { display: 'inline' } : undefined}
            >
                <MDXEditor
                    markdown={sanitizeForMdx(value || '')}
                    readOnly
                    plugins={plugins}
                    className="dark-theme dark-editor"
                    contentEditableClassName="mdx-viewer-content"
                />
            </MDXViewerWrapper>
        );
    }

    const plugins = [
        headingsPlugin(),
        listsPlugin(),
        quotePlugin(),
        linkPlugin(),
        linkDialogPlugin({ showLinkTitleField: false }),
        thematicBreakPlugin(),
        codeBlockPlugin({
            defaultCodeBlockLanguage: '',
            codeBlockEditorDescriptors: [
                mermaidCodeBlockDescriptor,
                defaultCodeBlockDescriptor
            ]
        }),
        tablePlugin(),
        markdownShortcutPlugin(),
        toolbarPlugin({ toolbarContents })
    ];

    return (
        <>
            {mdxPortalStyles}
            <MDXEditorWrapper
                ref={wrapperRef}
                className="mdx-editor-container"
                data-color-mode="dark"
                contentHeight={height}
            >
                <MDXEditor
                    ref={editorRef}
                    markdown={sanitizeForMdx(value || '')}
                    onChange={handleChange}
                    plugins={plugins}
                    readOnly={disabled}
                    className="dark-theme dark-editor"
                    contentEditableClassName="mdx-content-editable"
                />
            </MDXEditorWrapper>
        </>
    );
};

export default MarkdownEditor;
